/**
 * ============================================================================
 * USE ESCROW HOOK
 * ============================================================================
 * 
 * Custom hook for escrow operations
 */

import { useState, useCallback } from 'react';
import * as escrowService from '../services/escrow';

export const useEscrow = (escrowId = null) => {
  const [escrow, setEscrow] = useState(null);
  const [escrows, setEscrows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);

  /**
   * Get escrow by transaction
   */
  const getEscrowByTransaction = useCallback(async (transactionId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await escrowService.getEscrowByTransaction(transactionId);
      setEscrow(response.data);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to get escrow');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get escrow by ID
   */
  const getEscrow = useCallback(async (id = escrowId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await escrowService.getEscrow(id);
      setEscrow(response.data);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to get escrow');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [escrowId]);

  /**
   * Get escrow status
   */
  const getEscrowStatus = useCallback(async (id = escrowId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await escrowService.getEscrowStatus(id);
      if (id === escrowId) {
        setEscrow(response.data);
      }
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to get escrow status');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [escrowId]);

  /**
   * List user escrows
   */
  const listUserEscrows = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await escrowService.listUserEscrows(filters);
      setEscrows(response.data || []);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to list escrows');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Create escrow
   */
  const createEscrow = useCallback(async (data) => {
    try {
      setLoading(true);
      setError(null);
      const response = await escrowService.createEscrow(data);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to create escrow');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fund escrow
   */
  const fundEscrow = useCallback(async (id, data) => {
    try {
      setLoading(true);
      setError(null);
      const response = await escrowService.fundEscrow(id, data);
      if (id === escrowId) {
        setEscrow(response.data);
      }
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to fund escrow');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [escrowId]);

  /**
   * Release escrow
   */
  const releaseEscrow = useCallback(async (id, data) => {
    try {
      setLoading(true);
      setError(null);
      const response = await escrowService.releaseEscrow(id, data);
      if (id === escrowId) {
        setEscrow(response.data);
      }
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to release escrow');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [escrowId]);

  /**
   * Partial release escrow
   */
  const partialReleaseEscrow = useCallback(async (id, data) => {
    try {
      setLoading(true);
      setError(null);
      const response = await escrowService.partialReleaseEscrow(id, data);
      if (id === escrowId) {
        setEscrow(response.data);
      }
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to partially release escrow');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [escrowId]);

  /**
   * Refund escrow
   */
  const refundEscrow = useCallback(async (id, data) => {
    try {
      setLoading(true);
      setError(null);
      const response = await escrowService.refundEscrow(id, data);
      if (id === escrowId) {
        setEscrow(response.data);
      }
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to refund escrow');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [escrowId]);

  /**
   * Freeze escrow
   */
  const freezeEscrow = useCallback(async (id, data) => {
    try {
      setLoading(true);
      setError(null);
      const response = await escrowService.freezeEscrow(id, data);
      if (id === escrowId) {
        setEscrow(response.data);
      }
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to freeze escrow');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [escrowId]);

  /**
   * Unfreeze escrow
   */
  const unfreezeEscrow = useCallback(async (id) => {
    try {
      setLoading(true);
      setError(null);
      const response = await escrowService.unfreezeEscrow(id);
      if (id === escrowId) {
        setEscrow(response.data);
      }
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to unfreeze escrow');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [escrowId]);

  /**
   * Get escrow history
   */
  const loadHistory = useCallback(async (id = escrowId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await escrowService.getEscrowHistory(id);
      setHistory(response.data || []);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to load escrow history');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [escrowId]);

  return {
    // State
    escrow,
    escrows,
    history,
    loading,
    error,
    
    // Actions
    getEscrowByTransaction,
    getEscrow,
    getEscrowStatus,
    listUserEscrows,
    createEscrow,
    fundEscrow,
    releaseEscrow,
    partialReleaseEscrow,
    refundEscrow,
    freezeEscrow,
    unfreezeEscrow,
    loadHistory,
    
    // Convenience
    refresh: escrowId ? () => getEscrow(escrowId) : null,
  };
};

export default useEscrow;
